/**
 * 배관 압력손실 검토 화면.
 *
 * 세 가지를 한 화면에서 본다 — 표준이 서로 얽혀 있기 때문이다.
 *  1) 입구 3 % 판정 (7.3.4) — 넘으면 힘 평형 대안(7.3.6 d)이 자동으로 뜬다
 *  2) 출구 압력손실 = 축적배압 → 배압 판정 (5.3)
 *  3) 대기 방출 반력 (식 1)
 *
 * 출구 압력손실이 곧 축적배압이므로, 따로 입력받지 않고 계산 결과를
 * 그대로 배압 판정에 넘긴다 — 두 값이 어긋날 여지를 없앤다.
 */
import { useMemo } from "react";
import { FITTINGS, FITTING_KINDS, type FittingKind } from "../calc/fittings";
import { PIPE_ROUGHNESS_M } from "../calc/pipeFlow";
import type { BackpressureValveType } from "../calc/backpressure";
import type { InletFlowBasis, InletLossExemption } from "../calc/inletLoss";
import type { LossMethod } from "../calc/pipeFlow";
import { PIPE_SIZES, nearestSchedule, schedulesFor } from "../data/pipes";
import { Field, Help, Item } from "./formBits";
import { gaugeLabel, type PressureUnit } from "./pressureUnit";
import {
  addFitting,
  newPipeRun,
  runPiping,
  setFittingCount,
  MATERIAL_LABELS,
  type PipeMaterial,
  type PipeRunState,
  type PipingState,
  type RunHydraulics,
} from "./pipingState";

const nf = (v: number, d = 1) =>
  Number.isFinite(v)
    ? v.toLocaleString("ko-KR", {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
      })
    : "—";

const REGIME_LABEL = {
  laminar: "층류",
  transitional: "천이",
  turbulent: "난류",
} as const;

/** 배관 한 구간의 입력 + 결과. 입구·출구가 같은 모양을 쓴다. */
function PipeRunForm({
  title,
  note,
  value,
  result,
  onChange,
}: {
  title: string;
  note: string;
  value: PipeRunState;
  result: RunHydraulics | null;
  onChange: (next: PipeRunState) => void;
}) {
  const set = <K extends keyof PipeRunState>(k: K, v: PipeRunState[K]) =>
    onChange({ ...value, [k]: v });

  return (
    <div className="piperun">
      <div className="piperun__head">
        <h3>{title}</h3>
        <span className="muted">{note}</span>
      </div>

      <div className="grid-form">
        <Field label="호칭경" unit="in" help="pipeNps">
          <select
            value={value.nps}
            // 호칭경을 바꾸면 그 크기에 없는 스케줄이 남을 수 있다.
            onChange={(e) =>
              onChange({
                ...value,
                nps: e.target.value,
                schedule: nearestSchedule(e.target.value, value.schedule),
              })
            }
            disabled={value.useCustomId}
          >
            {PIPE_SIZES.map((s) => (
              <option key={s.nps} value={s.nps}>
                {s.nps}&quot;
              </option>
            ))}
          </select>
        </Field>
        <Field label="스케줄" unit="" help="pipeSchedule">
          <select
            value={value.schedule}
            onChange={(e) => set("schedule", e.target.value)}
            disabled={value.useCustomId}
          >
            {schedulesFor(value.nps).map((s) => (
              <option key={s} value={s}>
                Sch {s}
              </option>
            ))}
          </select>
        </Field>
        <Field label="재질·조도" unit="" help="pipeMaterial">
          <select
            value={value.material}
            onChange={(e) => set("material", e.target.value as PipeMaterial)}
          >
            {(Object.keys(PIPE_ROUGHNESS_M) as PipeMaterial[]).map((m) => (
              <option key={m} value={m}>
                {MATERIAL_LABELS[m]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="직관 길이" unit="m" help="straightLength">
          <input
            value={value.straightLengthM}
            onChange={(e) => set("straightLengthM", e.target.value)}
          />
        </Field>
        {value.useCustomId && (
          <Field label="내경 직접 입력" unit="mm" help="customInnerDiameter">
            <input
              value={value.customIdMm}
              onChange={(e) => set("customIdMm", e.target.value)}
            />
          </Field>
        )}
      </div>

      <label className="piperun__custom">
        <input
          type="checkbox"
          checked={value.useCustomId}
          onChange={(e) => set("useCustomId", e.target.checked)}
        />
        내경을 직접 넣기 (사내 배관 사양이 다를 때)
        <Help topic="customInnerDiameter" />
      </label>

      {/* ── 부속 ── */}
      <div className="piperun__fittings">
        <div className="row-head">
          <h4>
            부속
            <Help topic="fittingList" />
          </h4>
          <select
            value=""
            onChange={(e) => {
              const k = e.target.value as FittingKind;
              if (k) set("fittings", addFitting(value.fittings, k));
            }}
          >
            <option value="">+ 부속 추가…</option>
            {FITTING_KINDS.filter(
              (k) => !value.fittings.some((f) => f.kind === k),
            ).map((k) => (
              <option key={k} value={k}>
                {FITTINGS[k].label}
              </option>
            ))}
          </select>
        </div>

        {value.fittings.length === 0 && (
          <p className="hint">
            부속이 없습니다. 용기에서 배관으로 들어가는 <b>입구 손실</b>은
            표준이 반드시 포함하라고 합니다 (7.3.7.2 a).
          </p>
        )}

        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>부속</th>
                <th>개수</th>
                <th>L/D</th>
                <th>등가길이 (m)</th>
                <th>K</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {value.fittings.map((f) => {
                const spec = FITTINGS[f.kind];
                const row = result?.breakdown.find((b) => b.kind === f.kind);
                return (
                  <tr key={f.kind}>
                    <td>
                      {spec.label}
                      {spec.note && (
                        <>
                          <br />
                          <span className="muted">{spec.note}</span>
                        </>
                      )}
                    </td>
                    <td>
                      <input
                        className="cell-input"
                        type="number"
                        min={0}
                        value={f.count}
                        onChange={(e) =>
                          set(
                            "fittings",
                            setFittingCount(
                              value.fittings,
                              f.kind,
                              Number(e.target.value),
                            ),
                          )
                        }
                      />
                    </td>
                    <td>{spec.lOverD === null ? "—" : spec.lOverD}</td>
                    <td>{row ? nf(row.equivalentLengthM, 2) : "—"}</td>
                    <td>{row ? nf(row.k, 2) : "—"}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() =>
                          set(
                            "fittings",
                            value.fittings.filter((x) => x.kind !== f.kind),
                          )
                        }
                      >
                        삭제
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {result && (
        <dl className="result__grid">
          <Item label="내경" value={`${nf(result.innerDiameterMm, 2)} mm`} />
          <Item
            label="계산 길이"
            value={`${nf(result.totalLengthM, 2)} m (등가 ${nf(result.equivalentLengthM, 2)})`}
          />
          <Item label="ΣK (길이 미환산)" value={nf(result.fittingK, 2)} />
          <Item label="유속" value={`${nf(result.velocityMs, 2)} m/s`} />
          <Item
            label="레이놀즈수"
            value={`${result.reynolds.toExponential(2)} · ${REGIME_LABEL[result.flowRegime]}`}
          />
          <Item label="마찰계수 f" value={result.frictionFactor.toFixed(4)} />
          <Item
            label="압력손실"
            value={`${nf(result.dropKpa, 2)} kPa (길이 몫 ${nf(result.frictionKpa, 2)} + ΣK 몫 ${nf(result.fittingKpa, 2)})`}
          />
        </dl>
      )}
    </div>
  );
}

export function PipingPanel({
  value,
  unit,
  onChange,
}: {
  value: PipingState;
  unit: PressureUnit;
  onChange: (next: PipingState) => void;
}) {
  const patch = (p: Partial<PipingState>) => onChange({ ...value, ...p });
  const out = useMemo(() => runPiping(value, unit), [value, unit]);

  return (
    <>
      <section className="card">
        <h2>검토 조건</h2>
        <div className="grid-form">
          <Field label="태그" unit="" help="tag">
            <input value={value.tag} onChange={(e) => patch({ tag: e.target.value })} />
          </Field>
          <Field label="설정압" unit={gaugeLabel(unit)} help="setPressure">
            <input
              value={value.setPressure}
              onChange={(e) => patch({ setPressure: e.target.value })}
            />
          </Field>
          <Field label="MAWP" unit={gaugeLabel(unit)} help="mawpPiping">
            <input value={value.mawp} onChange={(e) => patch({ mawp: e.target.value })} />
          </Field>
          <Field label="허용 축적" unit="%" help="allowableAccumulation">
            <input
              value={value.allowableAccumulationPercent}
              onChange={(e) =>
                patch({ allowableAccumulationPercent: e.target.value })
              }
            />
          </Field>
          <Field label="밸브 형식" unit="" help="valveTypePiping">
            <select
              value={value.valveType}
              onChange={(e) =>
                patch({ valveType: e.target.value as BackpressureValveType })
              }
            >
              <option value="conventional">일반형 (Conventional)</option>
              <option value="balanced-bellows">밸런스드 벨로우즈</option>
              <option value="pilot-operated">파일럿형</option>
            </select>
          </Field>
          <Field label="유량" unit="kg/h" help="pipingFlow">
            <input
              value={value.flowKgH}
              onChange={(e) => patch({ flowKgH: e.target.value })}
            />
          </Field>
          <Field label="유량 기준" unit="" help="flowBasis">
            <select
              value={value.flowBasis}
              onChange={(e) =>
                patch({ flowBasis: e.target.value as InletFlowBasis })
              }
            >
              <option value="rated">정격 용량 (rated)</option>
              <option value="required">소요 유량 (required)</option>
            </select>
          </Field>
          <Field label="밀도" unit="kg/m³" help="pipingDensity">
            <input
              value={value.densityKgM3}
              onChange={(e) => patch({ densityKgM3: e.target.value })}
            />
          </Field>
          <Field label="점도" unit="cP" help="pipingViscosity">
            <input
              value={value.viscosityCp}
              onChange={(e) => patch({ viscosityCp: e.target.value })}
            />
          </Field>
          <Field label="손실 계산 방식" unit="" help="lossMethod">
            <select
              value={value.method}
              onChange={(e) => patch({ method: e.target.value as LossMethod })}
            >
              <option value="equivalent-length">등가길이 (L/D)</option>
              <option value="resistance-coefficient">저항계수 (K)</option>
            </select>
          </Field>
          <Field label="3 % 기준 예외" unit="" help="inletExemption">
            <select
              value={value.exemption}
              onChange={(e) =>
                patch({ exemption: e.target.value as InletLossExemption })
              }
            >
              <option value="none">해당 없음</option>
              <option value="thermal-relief">열팽창 전용 (7.3.8)</option>
              <option value="remote-sensed-pilot">
                원격감지 파일럿형 (7.3.9)
              </option>
            </select>
          </Field>
          <Field
            label="상류 파열판 압력강하"
            unit={gaugeLabel(unit)}
            help="ruptureDiskDrop"
          >
            <input
              value={value.ruptureDiskDrop}
              onChange={(e) => patch({ ruptureDiskDrop: e.target.value })}
            />
          </Field>
          <Field label="중첩배압" unit={gaugeLabel(unit)} help="superimposed">
            <input
              value={value.superimposed}
              onChange={(e) => patch({ superimposed: e.target.value })}
            />
          </Field>
        </div>
        <p className="hint">
          배관 치수는 ASME B36.10M 상용 치수입니다 — API 표준이 아닙니다.
          사내 배관 사양이 다르면 각 구간에서 <b>내경을 직접</b> 넣으십시오.
        </p>
      </section>

      {!out.ok && (
        <section className="card">
          <div className="result result--error" role="alert">
            {out.message}
          </div>
        </section>
      )}

      <section className="card">
        <PipeRunForm
          title="입구 배관"
          note="용기 → 밸브 입구"
          value={value.inlet}
          result={out.inlet}
          onChange={(inlet) => patch({ inlet })}
        />
      </section>

      {/* ── 입구 3 % 판정 ── */}
      {out.inletCheck?.ok && (
        <section className="card">
          <h2>입구 압력손실 판정 (3 % 기준)</h2>
          <div
            className={
              out.inletCheck.withinLimit
                ? "result result--ok"
                : "result result--error"
            }
          >
            <div className="result__headline">
              <span className="result__label">판정</span>
              <strong className="result__value">
                {out.inletCheck.withinLimit ? "적합" : "부적합"}
              </strong>
            </div>
            <dl className="result__grid">
              <Item
                label="배관 손실"
                value={`${nf(out.inletCheck.pipingLossKpa, 2)} kPa`}
              />
              <Item
                label="파열판 몫"
                value={`${nf(out.inletCheck.ruptureDiskLossKpa, 2)} kPa`}
              />
              <Item
                label="총 비회복 손실"
                value={`${nf(out.inletCheck.totalLossKpa, 2)} kPa`}
              />
              <Item
                label="설정압 대비"
                value={`${nf(out.inletCheck.lossPercent, 2)} %`}
              />
            </dl>
          </div>

          {out.inletCheck.warnings.length > 0 && (
            <ul className="warnings">
              {out.inletCheck.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}

          {/* 3 % 를 넘었을 때만 대안 판정을 보여준다 */}
          {out.forceBalance && (
            <>
              <h3>힘 평형에 의한 대안 판정 (7.3.6 d)</h3>
              <p className="hint">
                3 % 를 넘어도, 밸브가 채터링 없이 열려 있을 수 있음을 힘
                평형으로 보일 수 있으면 허용됩니다. 이 판정에는 표준이
                <b> 10 % 과압</b>을 쓰라고 합니다.
              </p>
              <div className="grid-form">
                <Field label="블로다운" unit={gaugeLabel(unit)} help="blowdown">
                  <input
                    value={value.blowdown}
                    onChange={(e) => patch({ blowdown: e.target.value })}
                  />
                </Field>
                <Field
                  label="판정용 과압"
                  unit={gaugeLabel(unit)}
                  help="forceBalanceOverpressure"
                >
                  <input
                    value={value.forceBalanceOverpressure}
                    onChange={(e) =>
                      patch({ forceBalanceOverpressure: e.target.value })
                    }
                  />
                </Field>
              </div>
              <div
                className={
                  out.forceBalance.passes
                    ? "result result--ok"
                    : "result result--error"
                }
              >
                <div className="result__headline">
                  <span className="result__label">힘 평형 판정</span>
                  <strong className="result__value">
                    {out.forceBalance.passes ? "만족" : "불만족"}
                  </strong>
                </div>
                <p className="muted">{out.forceBalance.criterion}</p>
                <dl className="result__grid">
                  <Item label="좌변" value={`${nf(out.forceBalance.leftSide, 2)} kPa`} />
                  <Item label="우변" value={`${nf(out.forceBalance.rightSide, 2)} kPa`} />
                </dl>
              </div>
              {out.forceBalance.warnings.length > 0 && (
                <ul className="warnings">
                  {out.forceBalance.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>
      )}

      <section className="card">
        <PipeRunForm
          title="출구 배관"
          note="밸브 출구 → 방출점·헤더"
          value={value.outlet}
          result={out.outlet}
          onChange={(outlet) => patch({ outlet })}
        />
        <p className="hint">
          출구 압력손실이 곧 <b>축적배압</b>입니다 — 아래 배압 판정에 그대로
          쓰이므로 따로 넣지 않으셔도 됩니다.
        </p>

        <div className="row-head">
          <h3>출구 배관 계산 방식</h3>
          <label className="scenario__check">
            <input
              type="checkbox"
              checked={value.outletCompressible}
              onChange={(e) =>
                patch({ outletCompressible: e.target.checked })
              }
            />
            등온 압축성으로 계산 (KOSHA 4.1(1))
          </label>
        </div>
        {value.outletCompressible ? (
          <>
            <p className="hint">
              배출배관은 압력이 내려가면서 기체가 팽창해 속도가 빨라집니다.
              비압축성 식은 이 팽창을 무시해 손실을 <b>과소평가</b>합니다.
              KOSHA GUIDE D-63-2018 4.1(1) 은 배출배관을 등온 과정으로
              보라고 하므로 이 방식을 기본으로 씁니다. 이때 밀도는 위의
              입력값 대신 아래 분자량·온도·압축계수로 지점마다 다시
              계산합니다.
            </p>
            <div className="grid-form">
              <Field label="분자량" unit="g/mol" help="outletMolecularWeight">
                <input
                  value={value.outletMolecularWeight}
                  onChange={(e) =>
                    patch({ outletMolecularWeight: e.target.value })
                  }
                />
              </Field>
              <Field label="배출 온도" unit="K" help="outletTemperatureK">
                <input
                  value={value.outletTemperatureK}
                  onChange={(e) =>
                    patch({ outletTemperatureK: e.target.value })
                  }
                />
              </Field>
              <Field label="압축계수 Z" unit="" help="outletCompressibility">
                <input
                  value={value.outletCompressibility}
                  onChange={(e) =>
                    patch({ outletCompressibility: e.target.value })
                  }
                />
              </Field>
            </div>
          </>
        ) : (
          <p className="hint">
            비압축성(Darcy–Weisbach)으로 계산합니다. 액체 배출이거나 출구
            배관이 아주 짧아 마하수가 낮을 때만 쓰십시오 — 기체가 긴
            배관을 지나면 손실을 크게 과소평가합니다.
          </p>
        )}
        {out.outlet?.compressible && (
          <dl className="result__grid">
            <Item
              label="출구단 마하수"
              value={nf(out.outlet.compressible.outletMach, 3)}
            />
            <Item
              label="입구단 마하수"
              value={nf(out.outlet.compressible.inletMach, 3)}
            />
            <Item
              label="출구단 속도"
              value={`${nf(out.outlet.compressible.outletVelocityMs, 1)} m/s`}
            />
            <Item
              label="출구단 밀도"
              value={`${nf(out.outlet.compressible.outletDensityKgM3, 3)} kg/m³`}
            />
            <Item
              label="관 입구 절대압"
              value={`${nf(out.outlet.compressible.inletPressureKpaAbs, 1)} kPa(a)`}
            />
            <Item
              label="관 출구 절대압"
              value={`${nf(out.outlet.compressible.outletPressureKpaAbs, 1)} kPa(a)`}
            />
            <Item
              label="초크 한계압"
              value={`${nf(out.outlet.compressible.criticalPressureKpaAbs, 1)} kPa(a)`}
            />
          </dl>
        )}
      </section>

      {/* ── 배압 판정 ── */}
      {out.backpressure?.ok && (
        <section className="card">
          <h2>배압 판정</h2>
          <div
            className={
              out.backpressure.builtUpWithinLimit &&
              out.backpressure.totalWithinLimit
                ? "result result--ok"
                : "result result--error"
            }
          >
            <div className="result__headline">
              <span className="result__label">판정</span>
              <strong className="result__value">
                {out.backpressure.builtUpWithinLimit &&
                out.backpressure.totalWithinLimit
                  ? "적합"
                  : "부적합"}
              </strong>
            </div>
            <p className="muted">{out.backpressure.criterion}</p>
            <dl className="result__grid">
              <Item
                label="축적배압 (출구 손실)"
                value={`${nf(out.outlet?.dropKpa ?? Number.NaN, 2)} kPa`}
              />
              <Item
                label="총 배압"
                value={`${nf(out.backpressure.totalKpaGauge, 2)} kPa`}
              />
              <Item
                label="총 배압 / 설정압"
                value={`${nf(out.backpressure.totalPercentOfSet, 1)} %`}
              />
              <Item
                label="축적배압 / 설정압"
                value={`${nf(out.backpressure.builtUpPercentOfSet, 1)} %`}
              />
              <Item
                label="허용 축적배압"
                value={
                  out.backpressure.allowableBuiltUpKpa === null
                    ? "— (형식별 다른 기준)"
                    : `${nf(out.backpressure.allowableBuiltUpKpa, 2)} kPa`
                }
              />
            </dl>
          </div>
          {out.backpressure.warnings.length > 0 && (
            <ul className="warnings">
              {out.backpressure.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* ── 반력 ── */}
      <section className="card">
        <div className="row-head">
          <h2>대기 방출 반력</h2>
          <label className="scenario__check">
            <input
              type="checkbox"
              checked={value.reaction.enabled}
              onChange={(e) =>
                patch({
                  reaction: { ...value.reaction, enabled: e.target.checked },
                })
              }
            />
            계산하기
            <Help topic="reactionEnabled" />
          </label>
        </div>

        {value.reaction.enabled && (
          <>
            <div className="grid-form">
              <Field label="비열비 k" unit="" help="specificHeatRatio">
                <input
                  value={value.reaction.specificHeatRatio}
                  onChange={(e) =>
                    patch({
                      reaction: {
                        ...value.reaction,
                        specificHeatRatio: e.target.value,
                      },
                    })
                  }
                />
              </Field>
              <Field label="정체온도 T" unit="K" help="stagnationTemperature">
                <input
                  value={value.reaction.stagnationTemperatureK}
                  onChange={(e) =>
                    patch({
                      reaction: {
                        ...value.reaction,
                        stagnationTemperatureK: e.target.value,
                      },
                    })
                  }
                />
              </Field>
              <Field label="분자량" unit="kg/kmol" help="molecularWeight">
                <input
                  value={value.reaction.molecularWeight}
                  onChange={(e) =>
                    patch({
                      reaction: {
                        ...value.reaction,
                        molecularWeight: e.target.value,
                      },
                    })
                  }
                />
              </Field>
              <Field label="방출점 내경" unit="mm" help="reactionOutletId">
                <input
                  value={value.reaction.outletIdMm}
                  onChange={(e) =>
                    patch({
                      reaction: { ...value.reaction, outletIdMm: e.target.value },
                    })
                  }
                />
              </Field>
              <Field
                label="방출 직전 정압"
                unit={gaugeLabel(unit)}
                help="reactionStaticPressure"
              >
                <input
                  value={value.reaction.outletStaticPressure}
                  onChange={(e) =>
                    patch({
                      reaction: {
                        ...value.reaction,
                        outletStaticPressure: e.target.value,
                      },
                    })
                  }
                />
              </Field>
            </div>
            <label className="piperun__custom">
              <input
                type="checkbox"
                checked={value.reaction.usedRelievingTemperature}
                onChange={(e) =>
                  patch({
                    reaction: {
                      ...value.reaction,
                      usedRelievingTemperature: e.target.checked,
                    },
                  })
                }
              />
              정체온도 대신 배출온도를 넣었음
              <Help topic="usedRelievingTemperature" />
            </label>

            {out.reaction?.ok && (
              <div className="result result--ok">
                <div className="result__headline">
                  <span className="result__label">반력 F</span>
                  <strong className="result__value">
                    {nf(out.reaction.forceN, 0)} N
                  </strong>
                </div>
                <dl className="result__grid">
                  <Item
                    label="운동량 몫"
                    value={`${nf(out.reaction.momentumN, 0)} N`}
                  />
                  <Item
                    label="정압 몫"
                    value={`${nf(out.reaction.staticN, 0)} N`}
                  />
                  <Item label="적용 식" value={out.reaction.equation} />
                </dl>
              </div>
            )}
            {out.reaction && !out.reaction.ok && (
              <div className="result result--error">{out.reaction.message}</div>
            )}
            {out.reaction?.ok && out.reaction.warnings.length > 0 && (
              <ul className="warnings">
                {out.reaction.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>

      {out.warnings.length > 0 && (
        <section className="card">
          <ul className="warnings">
            {out.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

export { newPipeRun };
